import { useState } from 'react'

import { Box, Title } from '@factorialco/deprecated-design-system'
import { endOfMonth, startOfMonth } from 'date-fns'
import { BenefitsUsagePageEmployeeIdsDocument } from 'generated/resources/graphql'
import _ from 'lodash'

import { DateNavigator } from 'modules/core/components/DateNavigator'
import Loading from 'modules/core/components/Loading'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { UsageDataCollection } from './UsageDataCollection'

const SpendingPage = () => {
  useEffect(() => {
    track(trackingEvents.benefits.openBenefitsUsagePage)
  }, [])

  // Filter management
  const [range, setRange] = useState<[Date, Date]>([
    startOfMonth(new Date()),
    endOfMonth(new Date()),
  ])

  function handleChangeMonth(newStartDate: Date, newEndDate: Date) {
    // Disregard future periods.
    if (newStartDate <= startOfMonth(new Date())) {
      setRange([newStartDate, newEndDate])
    }
  }

  // Data management

  // Fetching in two passes to ensure we can search and filter by employee name.
  // Since we don't have a resource to identify benefit employees this works
  // backwards to establish first who, and then fetches the full graph.
  const { data: employeeIdsData, loading: employeeIdsLoading } = useQuery(
    BenefitsUsagePageEmployeeIdsDocument,
    {
      variables: {
        startDate: range[0].toISOString(),
        endDate: range[1].toISOString(),
      },
      fetchPolicy: 'no-cache',
    }
  )

  const employeeIds = useUnsafeMemo(() => {
    return _.uniq(
      employeeIdsData?.benefits.benefitCompensationsConnection.nodes.map(
        (c) => c.employeeName?.id ?? 0
      )
    )
  }, [employeeIdsData])

  return (
    <ResponsivePage.Body>
      <Box gap="s32">
        <Box gap="s8">
          <Title
            variant="400"
            title={i18n.t('benefits.tabs.usage.page.title')}
            description={i18n.t('benefits.tabs.usage.page.description')}
          />
          <DateNavigator
            regularPeriods={['month']}
            startDate={range[0]}
            endDate={range[1]}
            onDateRangeChange={handleChangeMonth}
          />
        </Box>

        {/* HACK: This is a band-aid while we investigate a deficiency in the
        useGraphqlDataSource hook, which isn't refetching when the dependencies
          provided the hook change. When that is fixed we should remove this
          wrapper component*/}
        {employeeIdsLoading ? (
          <Loading />
        ) : (
          <UsageDataCollection employeeIds={employeeIds} startDate={range[0]} endDate={range[1]} />
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default SpendingPage
